import { useState } from 'react'
import CheckBox from 'components/CheckBox'
import PhoneInput from 'components/Inputs/PhoneInput'
import MaskedInputService from 'services/maskedInput'
import MainButton from 'components/Buttons/MainButton'
import submitOnEnter from 'utils/submitOnEnter'
import businessDataValidator from 'validators/businessDataValidator'
import AddPictureModal from 'pages/Modals/AddPictureModal'
import cutileiLogo from 'icons/cutilei-logo.png'
import { MdNavigateNext } from 'react-icons/md'
import { Formik } from 'formik'

import {
  Form,
  Row,
  CheckRow,
  Label,
  SubTitle,
  InfoText,
  FormField,
  WarningText,
  ImageContainer,
  Image,
  Input,
  MaskedInput
} from '../styles'

export default function BusinessDataForm ({
  businessData = {},
  socialMedias = [],
  onFormSubmitted: handleFormSubmitted = () => null
}) {
  const [showPictureDialog, setShowPictureDialog] = useState (false)

  const handlePreventEvent = e => e.preventDefault ()
  const togglePictureDialog = () => setShowPictureDialog (!showPictureDialog)

  return (
    <Formik
      validationSchema={businessDataValidator}
      validateOnChange={false}
      validateOnBlur={true}
      initialValues={{
        'parent_id': businessData.parent_id,
        'logo': businessData.logo,
        'name': businessData.name,
        'corporate_name': businessData.corporate_name,
        'use_cnpj': true,
        'cnpj': businessData.cnpj,
        'email': businessData.email,
        'email_confirmation': businessData.email_confirmation,
        'phone': businessData.phone,
        'cellphone': businessData.cellphone,
        'social_medias': socialMedias.map (socialMedia => ({
          id: socialMedia.id,
          name: socialMedia.name,
          base_url: socialMedia.base_url,
          link: ''
        }))
      }}
      onSubmit={values => {
        const social_medias = values.social_medias.filter (socialMedia => (
          socialMedia.link !== '' && socialMedia.link !== null
        ))

        handleFormSubmitted ({ ...values, social_medias })
      }}
    >
      {({
        values,
        errors,
        touched,
        setFieldValue,
        setFieldTouched,
        handleChange,
        handleBlur,
        handleSubmit
      }) => {
        const handleChangePicture = newPictureUrl => {
          setFieldValue ('logo', newPictureUrl)
        }

        const handleDocumentTypeChange = useCnpj => {
          if (values.use_cnpj === useCnpj) return
          setFieldValue ('cnpj', '')
          setFieldValue ('use_cnpj', !values.use_cnpj)
        }

        return (
          <Form onKeyDown={submitOnEnter(handleSubmit)}>
            <SubTitle>Dados do salão</SubTitle>
            <FormField style={{marginBottom: 20}}>
              <ImageContainer onClick={togglePictureDialog}>
                <Image src={values.logo?.url ?? cutileiLogo}/>
              </ImageContainer>
              <SubTitle>Adicione a logo de seu salão</SubTitle>
              <InfoText>
                Sua logo será exibida em seu sistema e na tela de agendamentos do app dos membros da <b>Cutilei</b>.
              </InfoText>
              <InfoText>
                Dê preferência para uma foto em formato <b>retangular</b> com sua <b>logomarca</b>. 
              </InfoText>
            </FormField>
            <Row style={{alignItems: 'flex-end'}}>
              <FormField stretch={2}>
                <Input
                  placeholder='Nome do salão'
                  value={values.name}
                  onChange={handleChange ('name')}
                  onBlur={handleBlur ('name')}
                />
                {touched.name && errors.name && (
                  <WarningText>{errors.name}</WarningText>
                )}
              </FormField>
              <FormField stretch={2}>
                <Input
                  placeholder='Razão social'
                  value={values.corporate_name}
                  onChange={handleChange ('corporate_name')}
                  onBlur={handleBlur ('corporate_name')}
                />
                {touched.corporate_name && errors.corporate_name && (
                  <WarningText>{errors.corporate_name}</WarningText>
                )}
              </FormField>
              <FormField stretch={2}>
                <Row style={{gap: 12, marginBottom: 5}}>
                  <CheckRow>
                    <CheckBox
                      id='use_cnpj'
                      value={values.use_cnpj}
                      onValueChange={() => handleDocumentTypeChange (true)}
                    />
                    <Label htmlFor='use_cnpj'>Usar CNPJ</Label>
                  </CheckRow>
                  <CheckRow>
                    <CheckBox
                      id='use_cpf'
                      value={!values.use_cnpj}
                      onValueChange={() => handleDocumentTypeChange (false)}
                    />
                    <Label htmlFor='use_cpf'>Usar CPF</Label>
                  </CheckRow>
                </Row>
                <MaskedInput
                  key={values.use_cnpj ? 'cnpj' : 'cpf'}
                  name='cnpj'
                  placeholder={values.use_cnpj ? 'CNPJ' : 'CPF'}
                  options={{
                    blocks: values.use_cnpj ? [2,3,3,4,2] : [3,3,3,2],
                    delimiters: values.use_cnpj ? ['.', '.','/', '-'] : ['.', '.', '-'],
                    numericOnly: true
                  }}
                  value={values.cnpj}
                  onChange={event => MaskedInputService.handleChange (event, setFieldValue)}
                  onBlur={handleBlur ('cnpj')}
                />
                {touched.cnpj && errors.cnpj && (
                  <WarningText>{errors.cnpj}</WarningText>
                )}
              </FormField>
            </Row>
            <Row style={{width: 640}}>
              <FormField stretch={2}>
                <Input
                  type='email'
                  autoComplete='off'
                  placeholder='E-mail'
                  value={values.email}
                  onCopy={handlePreventEvent} 
                  onPaste={handlePreventEvent} 
                  onCut={handlePreventEvent} 
                  onChange={handleChange ('email')}
                  onBlur={handleBlur ('email')}
                />
                {touched.email && errors.email && (
                  <WarningText>{errors.email}</WarningText>
                )}
              </FormField>
              <FormField stretch={2}>
                <Input
                  type='email'
                  autoComplete='off'
                  placeholder='Confirmar e-mail'
                  value={values.email_confirmation}
                  onCopy={handlePreventEvent} 
                  onPaste={handlePreventEvent} 
                  onCut={handlePreventEvent} 
                  onChange={handleChange ('email_confirmation')}
                  onBlur={handleBlur ('email_confirmation')}
                />
                {touched.email_confirmation && errors.email_confirmation && (
                  <WarningText>{errors.email_confirmation}</WarningText>
                )}
              </FormField>
            </Row>
            <Row style={{width: 430}}>
              <FormField>
                <PhoneInput
                  name='cellphone'
                  placeholder='Celular'
                  value={values.cellphone}
                  onChange={setFieldValue}
                  onBlur={setFieldTouched}
                />
                {touched.cellphone && errors.cellphone && (
                  <WarningText>{errors.cellphone}</WarningText>
                )}
              </FormField>
              <FormField>
                  <PhoneInput
                  name='phone'
                  placeholder='Tel. fixo (opcional)'
                  value={values.phone}
                  onChange={setFieldValue}
                  onBlur={setFieldTouched}
                />
                {touched.phone && errors.phone && (
                  <WarningText>{errors.phone}</WarningText>
                )}
              </FormField>
            </Row>
            <SubTitle>Redes sociais do salão (opcional)</SubTitle>
            <Row>
              <InfoText>
                Os clientes poderão acessar as redes sociais do salão através do app da Cutilei
              </InfoText>
            </Row>
            <Row>
              {socialMedias.map ((socialMedia, index) => (
                <FormField key={index} stretch={2}>
                  <Input
                    name={`social_medias[${index}].link`}
                    placeholder={socialMedia.name}
                    value={values.social_medias[index].link}
                    onChange={handleChange (`social_medias[${index}].link`)}
                    onBlur={handleBlur (`social_medias[${index}].link`)}
                  />
                </FormField>
              ))}
            </Row>
            <AddPictureModal
              title='Adicionar foto de perfil'
              visible={showPictureDialog}
              currentImage={values.logo?.url ?? cutileiLogo}
              onConfirm={handleChangePicture}
              onClose={togglePictureDialog}
            />
            <MainButton
              leftIcon={<MdNavigateNext size={30}/>}
              aria-label='Próximo'
              onClick={handleSubmit}
              width={60}
            />
          </Form>
        )
      }}
    </Formik>
  )
}
